/**
 * FailedTreatmentsChecklist Component
 * 
 * Checkbox list for documenting failed conservative treatments.
 * One of the most common payer denial triggers - critical for appeals.
 */

import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Stack,
  Paper,
  Tooltip,
  Chip,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

export type FailedTreatmentType = 
  | 'PhysicalTherapy'
  | 'Bracing'
  | 'Medications'
  | 'Injections'
  | 'ActivityModification'
  | 'Other';

export interface FailedTreatment {
  type: FailedTreatmentType;
  selected: boolean;
  details?: string;
  duration?: string;
}

interface FailedTreatmentsChecklistProps {
  value: FailedTreatment[];
  onChange: (treatments: FailedTreatment[]) => void;
  disabled?: boolean;
  showDetails?: boolean;
}

const TREATMENT_INFO: Record<FailedTreatmentType, { label: string; placeholder: string; examples: string }> = {
  PhysicalTherapy: {
    label: 'Physical Therapy',
    placeholder: 'e.g., 6 weeks of PT, 2-3x/week',
    examples: '6-12 weeks typical duration',
  },
  Bracing: {
    label: 'Bracing / Splinting',
    placeholder: 'e.g., Wrist splint worn at night for 8 weeks',
    examples: 'Wrist/elbow splint, LSO brace, cervical collar',
  },
  Medications: {
    label: 'Medications',
    placeholder: 'e.g., NSAIDs, muscle relaxants, gabapentin',
    examples: 'NSAIDs, steroids, neuropathic agents',
  },
  Injections: {
    label: 'Injections',
    placeholder: 'e.g., 2 epidural steroid injections, minimal relief',
    examples: 'ESI, facet blocks, trigger point, joint injections',
  },
  ActivityModification: {
    label: 'Activity Modification',
    placeholder: 'e.g., Work restrictions, ergonomic changes',
    examples: 'Rest, ergonomics, lifting restrictions',
  },
  Other: {
    label: 'Other Treatments',
    placeholder: 'e.g., Chiropractic care, acupuncture',
    examples: 'Chiropractic, massage, TENS, acupuncture',
  },
};

const DEFAULT_TREATMENTS: FailedTreatment[] = [
  { type: 'PhysicalTherapy', selected: false },
  { type: 'Bracing', selected: false },
  { type: 'Medications', selected: false },
  { type: 'Injections', selected: false },
  { type: 'ActivityModification', selected: false },
  { type: 'Other', selected: false },
];

export const FailedTreatmentsChecklist: React.FC<FailedTreatmentsChecklistProps> = ({
  value = DEFAULT_TREATMENTS,
  onChange,
  disabled = false,
  showDetails = true,
}) => {
  // Ensure all treatment types are present
  const treatments = DEFAULT_TREATMENTS.map(defaultTx => {
    const existing = value.find(t => t.type === defaultTx.type);
    return existing ?? defaultTx;
  });

  const handleToggle = (type: FailedTreatmentType) => {
    onChange(
      treatments.map(t =>
        t.type === type ? { ...t, selected: !t.selected } : t
      )
    );
  };

  const handleDetailsChange = (type: FailedTreatmentType, details: string) => {
    onChange(
      treatments.map(t =>
        t.type === type ? { ...t, details } : t
      )
    );
  };

  const handleDurationChange = (type: FailedTreatmentType, duration: string) => {
    onChange(
      treatments.map(t =>
        t.type === type ? { ...t, duration } : t
      )
    );
  };

  const selectedCount = treatments.filter(t => t.selected).length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Failed Conservative Treatments
        </Typography>
        <Tooltip title="Document all non-surgical treatments attempted before the procedure. This is one of the most common denial triggers - thorough documentation is critical.">
          <InfoIcon fontSize="small" color="action" />
        </Tooltip>
        {selectedCount > 0 && (
          <Chip 
            label={`${selectedCount} selected`} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
        )}
      </Stack>

      <FormGroup>
        {treatments.map((treatment) => {
          const info = TREATMENT_INFO[treatment.type];
          return (
            <Paper
              key={treatment.type}
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1,
                backgroundColor: treatment.selected ? '#f0f9ff' : '#fafafa',
                border: treatment.selected ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                transition: 'all 0.2s',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={treatment.selected}
                    onChange={() => handleToggle(treatment.type)}
                    disabled={disabled}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {info.label}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {info.examples}
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%', m: 0 }}
              />
              
              {treatment.selected && showDetails && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5, ml: 4 }}>
                  <TextField
                    size="small"
                    placeholder={info.placeholder}
                    value={treatment.details ?? ''}
                    onChange={(e) => handleDetailsChange(treatment.type, e.target.value)}
                    disabled={disabled}
                    sx={{ flex: 2 }}
                    label="Details"
                  />
                  <TextField
                    size="small"
                    placeholder="Duration"
                    value={treatment.duration ?? ''}
                    onChange={(e) => handleDurationChange(treatment.type, e.target.value)}
                    disabled={disabled}
                    sx={{ flex: 1, minWidth: 120 }}
                    label="Duration"
                  />
                </Stack>
              )}
            </Paper>
          );
        })}
      </FormGroup>

      {selectedCount === 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontStyle: 'italic' }}>
          ⚠️ No conservative treatments documented. Most payers require evidence of failed conservative care.
        </Typography>
      )}
    </Box>
  );
};
